import React, { useEffect, useMemo, useState } from 'react';
import { ApiClient } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { roleLabel, effectiveUiRole } from '../../utils/roleLabels';
import {
  UsersRound, Plus, Save, Trash2, RefreshCw, Building2, Search, Check, Minus, CircleDot, Info, ShieldAlert,
} from 'lucide-react';

// ═══════════════════════════════════════════════════════════════
// Intézményi csoportok & jogosultságok
// A szervezeti admin csoportot hoz létre (pl. „Óvoda”), kiválasztja a kapcsolt intézményt,
// a tagokat, és alpontonként finomítja a hatókört. A csoporttag csak a hatókörbe eső
// alpontokat látja és kezeli; aki egyetlen csoportnak sem tagja, annak nem változik semmi.
// A hatókör szabálya ugyanaz, mint a szerveren (worker/core/access-groups.js).
// ═══════════════════════════════════════════════════════════════

type Mode = 'auto' | 'include' | 'exclude';
interface CatalogItem { page_id: number; pont: string; title: string; record_count: number; entity_ids: number[] }
interface Catalog { pages: { id: number; title: string }[]; items: CatalogItem[]; users: any[]; entities: any[] }
interface Group {
  id: number; tenant_id: number; name: string; description?: string | null;
  linked_entity_id?: number | null; linked_entity_name?: string | null;
  entities?: { id: number; name: string }[]; entity_ids?: number[];
  members: { id: number; name: string; email: string; role: string }[];
  items: { page_id: number; pont: string; mode: 'include' | 'exclude' }[];
}

const key = (pageId: number, pont: string) => `${pageId}|${String(pont ?? '').trim()}`;
const natural = (a: string, b: string) => a.localeCompare(b, 'hu', { numeric: true, sensitivity: 'base' });

export const AccessGroupsView: React.FC = () => {
  const { tenant, role } = useAuth();
  const isSuper = role === 'superadmin' || role === 'tenant_admin';
  const [tenants, setTenants] = useState<{ id: number; name: string }[]>([]);
  const [tenantId, setTenantId] = useState<number | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  // szerkesztett állapot
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  // A csoport kapcsolt intézményei (több is lehet, pl. Óvoda + Bölcsőde)
  const [entityIds, setEntityIds] = useState<number[]>([]);
  const [memberIds, setMemberIds] = useState<number[]>([]);
  const [modes, setModes] = useState<Record<string, Mode>>({});
  const [filter, setFilter] = useState('');
  const [onlyInScope, setOnlyInScope] = useState(false);
  const [newName, setNewName] = useState('');

  // Szervezet kiválasztása
  useEffect(() => {
    (async () => {
      const res: any = await ApiClient.getTenants().catch(() => null);
      const list = (res?.data || []).filter((t: any) => Number(t.id) !== 1).map((t: any) => ({ id: Number(t.id), name: t.name }));
      setTenants(list);
      const own = tenant?.id && Number(tenant.id) !== 1 ? Number(tenant.id) : list[0]?.id ?? null;
      setTenantId(own);
    })();
  }, [tenant?.id]);

  const load = async (tid: number, keepSelected?: number | null) => {
    setLoading(true);
    try {
      const [g, c]: any[] = await Promise.all([ApiClient.getAccessGroups(tid), ApiClient.getAccessGroupCatalog(tid)]);
      if (!g.ok) throw new Error(g.error || 'A csoportok nem tölthetők be');
      if (!c.ok) throw new Error(c.error || 'Az alpontok nem tölthetők be');
      setGroups(g.data || []);
      setCatalog(c.data);
      const sel = keepSelected && (g.data || []).some((x: Group) => x.id === keepSelected) ? keepSelected : (g.data?.[0]?.id ?? null);
      setSelectedId(sel);
    } catch (e: any) {
      setMsg({ type: 'err', text: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (tenantId) { setMsg(null); load(tenantId); } }, [tenantId]);

  const selected = groups.find((g) => g.id === selectedId) || null;

  // A kiválasztott csoport betöltése a szerkesztőbe
  useEffect(() => {
    if (!selected) return;
    setName(selected.name);
    setDescription(selected.description || '');
    setEntityIds(selected.entity_ids ?? (selected.linked_entity_id ? [selected.linked_entity_id] : []));
    setMemberIds(selected.members.map((m) => m.id));
    const m: Record<string, Mode> = {};
    for (const it of selected.items) m[key(it.page_id, it.pont)] = it.mode;
    setModes(m);
  }, [selectedId, groups]);

  const pageTitle = (id: number) => catalog?.pages.find((p) => p.id === id)?.title || `Oldal #${id}`;

  const inScope = (it: CatalogItem) => {
    const m = modes[key(it.page_id, it.pont)] || 'auto';
    if (m === 'exclude') return false;
    if (m === 'include') return true;
    return it.entity_ids.some((e) => entityIds.includes(e));
  };

  const items = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (catalog?.items || [])
      .filter((it) => !q || it.pont.toLowerCase().includes(q) || it.title.toLowerCase().includes(q))
      .filter((it) => !onlyInScope || inScope(it))
      .sort((a, b) => (a.page_id - b.page_id) || natural(a.pont, b.pont));
  }, [catalog, filter, onlyInScope, modes, entityIds]);

  const scopeCount = (catalog?.items || []).filter(inScope).length;

  const setMode = (it: CatalogItem, m: Mode) => setModes((prev) => {
    const k = key(it.page_id, it.pont);
    const next = { ...prev };
    if (m === 'auto') delete next[k]; else next[k] = m;
    return next;
  });

  const createGroup = async () => {
    if (!tenantId || !newName.trim()) return;
    const r: any = await ApiClient.createAccessGroup({ tenant_id: tenantId, name: newName.trim() });
    if (!r.ok) { setMsg({ type: 'err', text: r.error || 'Nem sikerült létrehozni' }); return; }
    setNewName('');
    await load(tenantId, r.id);
    setMsg({ type: 'ok', text: 'A csoport létrejött. Válaszd ki az intézményt és a tagokat, majd mentsd.' });
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    setMsg(null);
    const r: any = await ApiClient.updateAccessGroup(selected.id, {
      name, description, linked_entity_ids: entityIds,
      member_ids: memberIds,
      items: Object.entries(modes).map(([k, mode]) => {
        const i = k.indexOf('|');
        return { page_id: Number(k.slice(0, i)), pont: k.slice(i + 1), mode };
      }),
    });
    setSaving(false);
    if (!r.ok) { setMsg({ type: 'err', text: r.error || 'A mentés nem sikerült' }); return; }
    setMsg({ type: 'ok', text: 'Mentve. A tagok a következő oldalbetöltéskor már csak a hatókört látják.' });
    if (tenantId) load(tenantId, selected.id);
  };

  const remove = async () => {
    if (!selected) return;
    if (!confirm(`Biztosan törlöd a(z) „${selected.name}” csoportot?\n\nA tagjai ezután ismét a szerepkörük szerinti teljes hozzáférést kapják.`)) return;
    const r: any = await ApiClient.deleteAccessGroup(selected.id);
    if (!r.ok) { setMsg({ type: 'err', text: r.error || 'A törlés nem sikerült' }); return; }
    if (tenantId) load(tenantId);
  };

  return (
    <div className="space-y-4">
      {/* Fejléc */}
      <div className="p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-emerald-600 text-white"><UsersRound className="w-6 h-6" /></div>
          <div>
            <h1 className="text-lg font-bold text-slate-900 dark:text-white">Intézményi csoportok & jogosultságok</h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Pl. „Óvoda” csoport: a tagjai csak az óvodához rendelt alpontokat látják és kezelik.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {(isSuper || tenants.length > 1) && (
            <select
              value={tenantId ?? ''}
              onChange={(e) => setTenantId(Number(e.target.value))}
              className="text-xs px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-700 dark:text-slate-200"
              aria-label="Szervezet"
            >
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
          <button type="button" onClick={() => tenantId && load(tenantId, selectedId)} className="p-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer" title="Frissítés">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="p-3 rounded-xl bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 text-xs text-sky-900 dark:text-sky-200 flex gap-2">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          A csoport hatóköre: a kiválasztott <strong>intézmény(ek)hez rendelt alpontok</strong>, plusz amit kézzel <strong>hozzáadsz</strong>, mínusz amit <strong>kiveszel</strong>.
          A tagok ezekben szerkeszthetnek, mellékletet tölthetnek fel és új bejegyzést vehetnek fel; más alpontot nem látnak, tömeges műveletet és exportot nem érnek el.
          Aki egyetlen csoportnak sem tagja, annak nem változik semmi. Admin nem tehető csoportba.
        </div>
      </div>

      {msg && (
        <div className={`p-3 rounded-xl text-xs border ${msg.type === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-950/30 dark:border-emerald-900 dark:text-emerald-200' : 'bg-rose-50 border-rose-200 text-rose-800 dark:bg-rose-950/30 dark:border-rose-900 dark:text-rose-200'}`}>
          {msg.text}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
        {/* Csoportlista */}
        <div className="space-y-2">
          <div className="p-3 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 flex gap-2">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') createGroup(); }}
              placeholder="Új csoport neve (pl. Óvoda)"
              className="flex-1 min-w-0 text-xs px-2.5 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-200"
            />
            <button type="button" onClick={createGroup} disabled={!newName.trim()} className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white text-xs font-semibold flex items-center gap-1 cursor-pointer">
              <Plus className="w-3.5 h-3.5" /> Új
            </button>
          </div>
          {groups.length === 0 && !loading && (
            <div className="p-4 text-xs text-slate-400 text-center rounded-2xl border border-dashed border-slate-200 dark:border-slate-800">Még nincs csoport.</div>
          )}
          {groups.map((g) => (
            <button
              key={g.id}
              type="button"
              onClick={() => setSelectedId(g.id)}
              className={`w-full text-left p-3 rounded-2xl border transition cursor-pointer ${g.id === selectedId ? 'border-emerald-400 bg-emerald-50/60 dark:bg-emerald-950/30 dark:border-emerald-700' : 'border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-slate-300'}`}
            >
              <div className="font-semibold text-sm text-slate-900 dark:text-white">{g.name}</div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-0.5">
                <Building2 className="w-3 h-3 shrink-0" />
                <span className="truncate">{(g.entities && g.entities.length ? g.entities.map((e) => e.name).join(', ') : g.linked_entity_name) || 'nincs intézmény'}</span>
              </div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                {g.members.length} tag · {g.items.length} kézi alpont
              </div>
            </button>
          ))}
        </div>

        {/* Szerkesztő */}
        {selected && catalog ? (
          <div className="space-y-4">
            <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="text-xs text-slate-600 dark:text-slate-300 space-y-1">
                  <span className="font-semibold">Csoport neve</span>
                  <input value={name} onChange={(e) => setName(e.target.value)} className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-white" />
                </label>
                <label className="text-xs text-slate-600 dark:text-slate-300 space-y-1">
                  <span className="font-semibold">Leírás</span>
                  <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Pl. az óvoda és a bölcsőde közzétételeit kezelő munkatársak" className="w-full text-sm px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-white" />
                </label>
              </div>
              <div className="space-y-1.5">
                <div className="text-xs text-slate-600 dark:text-slate-300">
                  <span className="font-semibold">Kapcsolt intézmények (automatikus hatókör)</span>
                  <span className="text-slate-400"> – több is kijelölhető; egyik sem = csak a kézi alpontok</span>
                </div>
                {catalog.entities.length === 0 ? (
                  <p className="text-xs text-slate-400">Nincs kapcsolt intézmény. A Kapcsolt Szervek & Intézmények oldalon vehetsz fel.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5" role="group" aria-label="Kapcsolt intézmények">
                    {catalog.entities.map((e: any) => {
                      const on = entityIds.includes(e.id);
                      return (
                        <button
                          key={e.id}
                          type="button"
                          aria-pressed={on}
                          onClick={() => setEntityIds((prev) => on ? prev.filter((x) => x !== e.id) : [...prev, e.id])}
                          className={`px-2.5 py-1.5 rounded-lg text-xs border flex items-center gap-1.5 cursor-pointer transition ${on ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-slate-300'}`}
                        >
                          {on ? <Check className="w-3.5 h-3.5" /> : <Building2 className="w-3.5 h-3.5 text-slate-400" />}
                          <span>{e.name}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Tagok */}
            <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-2">
              <h3 className="text-sm font-bold text-slate-900 dark:text-white">Tagok <span className="text-xs font-normal text-slate-400">({memberIds.length})</span></h3>
              {catalog.users.length === 0 ? (
                <p className="text-xs text-slate-400">A szervezetben nincs nem admin felhasználó. Előbb vegyél fel szerkesztőt vagy ügyintézőt a Felhasználók oldalon.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {catalog.users.map((u: any) => {
                    const on = memberIds.includes(u.id);
                    return (
                      <button
                        key={u.id}
                        type="button"
                        onClick={() => setMemberIds((prev) => on ? prev.filter((x) => x !== u.id) : [...prev, u.id])}
                        className={`px-2.5 py-1.5 rounded-lg text-xs border flex items-center gap-1.5 cursor-pointer transition ${on ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-slate-300'} ${u.active === 0 ? 'opacity-50' : ''}`}
                        title={u.email}
                      >
                        {on ? <Check className="w-3.5 h-3.5" /> : <span className="w-3.5 h-3.5 rounded border border-slate-300 dark:border-slate-600 inline-block" />}
                        <span>{u.name}</span>
                        <span className={`text-[10px] ${on ? 'text-emerald-100' : 'text-slate-400'}`}>{roleLabel(effectiveUiRole(u))}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Alpontok */}
            <div className="p-4 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 space-y-3">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-2">
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                  Alpontok <span className="text-xs font-normal text-slate-500">– a hatókörben: <strong className="text-emerald-700 dark:text-emerald-400">{scopeCount}</strong> / {catalog.items.length}</span>
                </h3>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Pontszám vagy cím..." className="text-xs pl-8 pr-2.5 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-800 dark:text-slate-200 w-52" />
                  </div>
                  <label className="text-xs text-slate-600 dark:text-slate-300 flex items-center gap-1 cursor-pointer select-none">
                    <input type="checkbox" checked={onlyInScope} onChange={(e) => setOnlyInScope(e.target.checked)} className="accent-emerald-600" />
                    csak a hatókör
                  </label>
                </div>
              </div>
              {entityIds.length === 0 && Object.keys(modes).length === 0 && (
                <div className="p-2.5 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-[11px] text-amber-800 dark:text-amber-200 flex items-center gap-1.5">
                  <ShieldAlert className="w-3.5 h-3.5" /> Nincs intézmény és kézi alpont sem: a tagok jelenleg egyetlen alpontot sem látnának.
                </div>
              )}
              <div className="max-h-[55vh] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800 border border-slate-100 dark:border-slate-800 rounded-xl">
                {items.map((it, idx) => {
                  const k = key(it.page_id, it.pont);
                  const m = modes[k] || 'auto';
                  const viaEntity = it.entity_ids.some((e) => entityIds.includes(e));
                  const ok = inScope(it);
                  const showPage = idx === 0 || items[idx - 1].page_id !== it.page_id;
                  return (
                    <React.Fragment key={k}>
                      {showPage && (
                        <div className="px-3 py-1.5 bg-slate-50 dark:bg-slate-950 text-[11px] font-bold uppercase tracking-wide text-slate-500 sticky top-0 z-[1]">{pageTitle(it.page_id)}</div>
                      )}
                      <div className={`px-3 py-2 flex flex-col md:flex-row md:items-center gap-2 ${ok ? 'bg-emerald-50/40 dark:bg-emerald-950/10' : ''}`}>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {ok ? <CircleDot className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> : <Minus className="w-3.5 h-3.5 text-slate-300 shrink-0" />}
                          <span className="font-mono text-[11px] font-bold px-1.5 py-0.5 rounded bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 shrink-0 max-w-[14rem] truncate" title={it.pont}>{it.pont || '(pontszám nélkül)'}</span>
                          <span className="text-xs text-slate-700 dark:text-slate-300 truncate" title={it.title}>{it.title || '–'}</span>
                          {viaEntity && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 shrink-0">intézményé</span>}
                        </div>
                        <div className="flex items-center gap-1 shrink-0" role="radiogroup" aria-label={`${it.pont} hatóköre`}>
                          {([['auto', 'Automatikus'], ['include', 'Hozzáadva'], ['exclude', 'Kivéve']] as [Mode, string][]).map(([v, label]) => (
                            <button
                              key={v}
                              type="button"
                              role="radio"
                              aria-checked={m === v}
                              onClick={() => setMode(it, v)}
                              className={`px-2 py-1 rounded-md text-[11px] border cursor-pointer transition ${m === v
                                ? v === 'include' ? 'bg-emerald-600 border-emerald-600 text-white' : v === 'exclude' ? 'bg-rose-600 border-rose-600 text-white' : 'bg-slate-800 border-slate-800 text-white dark:bg-slate-200 dark:text-slate-900'
                                : 'bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-700 text-slate-500 hover:border-slate-300'}`}
                              title={v === 'auto' ? 'Az intézmény-hozzárendelés dönt' : v === 'include' ? 'Mindenképp a hatókörben' : 'Semmiképp nincs a hatókörben'}
                            >
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
                {items.length === 0 && <div className="p-4 text-xs text-slate-400 text-center">Nincs a szűrésnek megfelelő alpont.</div>}
              </div>
            </div>

            <div className="flex items-center justify-between gap-2">
              <button type="button" onClick={remove} className="px-3 py-2 rounded-xl text-xs text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 border border-transparent hover:border-rose-200 flex items-center gap-1.5 cursor-pointer">
                <Trash2 className="w-3.5 h-3.5" /> Csoport törlése
              </button>
              <button type="button" onClick={save} disabled={saving || !name.trim()} className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-xs font-semibold flex items-center gap-2 shadow-md cursor-pointer">
                <Save className="w-4 h-4" /> {saving ? 'Mentés...' : 'Csoport mentése'}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-8 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800 text-center text-xs text-slate-400">
            {loading ? 'Betöltés...' : 'Hozz létre egy csoportot, vagy válassz egyet a listából.'}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccessGroupsView;
